import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Trash2, AlertCircle, Mail, FileText, Zap, Activity, MessageSquare, Video } from 'lucide-react';
import { api } from './api';
import { useAuth } from '../../../context/AuthContext';
import StatusCockpit from './StatusCockpit';
import MailsTab from './MailsTab';
import NotesTab from './NotesTab';
import WorkflowTab from './WorkflowTab';
import ActivitiesTab from './ActivitiesTab';
import MeetingsTab from './MeetingsTab';
import NextActions from './NextActions';
import Sidebar from './Sidebar';
import FloatingActionButton from './FloatingActionButton';
import ComposeModal from './ComposeModal';

const TABS = [
  { id: 'mails',      label: 'E-Mails',     icon: Mail },
  { id: 'notes',      label: 'Notizen',     icon: FileText },
  { id: 'meetings',   label: 'Meetings',    icon: Video },
  { id: 'workflow',   label: 'Workflow',    icon: Zap },
  { id: 'activities', label: 'Aktivitäten', icon: Activity },
];

export default function LeadDetail() {
  const { id: leadId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('mails');
  const [counts, setCounts] = useState({ total: 0, unread_inbound: 0 });
  const [aiSummary, setAiSummary] = useState(null);

  const [compose, setCompose] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const loadLead = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api(`/api/leads/${leadId}`);
      setLead(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    loadLead();
    // #1054 — Sidebar-Counter neu laden, da GET /api/leads/:id Mails serverseitig auto-mark-read setzt
    try { window.dispatchEvent(new CustomEvent('weship:mail-changed')); } catch { /* noop */ }
  }, [loadLead]);

  const refresh = () => {
    loadLead();
    setRefreshKey((k) => k + 1);
    try { window.dispatchEvent(new CustomEvent('weship:mail-changed')); } catch { /* noop */ }
  };

  const handleAction = async (id) => {
    if (id === 'email')   setCompose({ replyTo: null });
    if (id === 'note')    setActiveTab('notes');
    if (id === 'meeting') setActiveTab('meetings');
    if (id === 'workflow') setActiveTab('workflow');
    if (id === 'task')    setActiveTab('activities');
    if (id === 'stage')   { /* stage pill in cockpit */ window.scrollTo({ top: 0, behavior: 'smooth' }); }
    if (id === 'owner')   { /* owner pill in cockpit */ window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };

  const onReply = (mail) => {
    setCompose({ replyTo: mail });
  };

  const handleDelete = async () => {
    try {
      await api(`/api/leads/${leadId}`, { method: 'DELETE' });
      navigate('/sales/anfragen');
    } catch (e) {
      setError(e.message);
      setConfirmDelete(false);
    }
  };

  const handleRerunAnalyse = async () => {
    try {
      await api(`/api/leads/${leadId}/analyse`, { method: 'POST' });
      refresh();
    } catch (e) {
      alert(e.message);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="animate-spin h-8 w-8 border-3 border-[#338edc] border-t-transparent rounded-full" />
    </div>
  );

  if (!lead) return (
    <div className="flex flex-col items-center justify-center h-full gap-4">
      <AlertCircle className="w-12 h-12 text-[var(--text-tertiary)]" />
      <p className="text-[var(--text-secondary)]">{error || 'Lead nicht gefunden'}</p>
      <button onClick={() => navigate('/sales/anfragen')} className="text-[#338edc] hover:underline text-[13px]">Zurück zur Liste</button>
    </div>
  );

  // #1047 Opt 6 — last_contact aus conversation-summary (echter letzter Mail-Kontakt),
  // Fallback auf lead.updated_at wenn AI-Summary noch nicht geladen.
  const summary = {
    last_contact: aiSummary?.last_contact || lead.updated_at,
    days_since_last_activity: aiSummary?.days_since_last_activity ?? lead.days_since_last_activity,
  };

  return (
    <div className="flex flex-col h-full bg-[var(--bg-app)]">
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[var(--border-card)] bg-[var(--bg-card)] flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => navigate('/sales/anfragen')}
            className="p-1.5 rounded hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)] flex-shrink-0"
            title="Zurück"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <nav className="text-[12px] text-[var(--text-tertiary)] hidden sm:flex items-center gap-1.5">
            <button onClick={() => navigate('/sales/anfragen')} className="hover:text-[#338edc]">Sales</button>
            <span>›</span>
            <button onClick={() => navigate('/sales/anfragen')} className="hover:text-[#338edc]">Anfragen</button>
            <span>›</span>
          </nav>
          <h1 className="text-[15px] sm:text-[16px] font-semibold text-[var(--text-primary)] truncate">
            {lead.firma || lead.ansprechpartner || `Lead #${lead.id}`}
          </h1>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => setConfirmDelete(true)}
            className="p-2 rounded text-[var(--text-tertiary)] hover:text-red-500 hover:bg-red-50"
            title="Löschen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </header>

      {error && (
        <div className="mx-4 sm:mx-6 mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-[13px]">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="p-0.5 hover:bg-red-100 rounded">×</button>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        <main className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6 space-y-4 max-w-[1100px]">
            <StatusCockpit lead={lead} summary={summary} refreshKey={refreshKey} onChanged={refresh} onSummaryLoaded={setAiSummary} />

            {/* #1054 — Original-Anfrage prominent unter dem Cockpit */}
            {lead.nachricht && (
              <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="w-4 h-4 text-[#338edc]" />
                  <h3 className="text-[12px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                    Original-Anfrage
                  </h3>
                </div>
                <div className="text-[13px] text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed">
                  {lead.nachricht}
                </div>
              </div>
            )}

            {/* #1083 Phase 4/E — AI-Next-Actions Chips über dem Tab-Bar */}
            <NextActions leadId={leadId} onAction={(a) => {
              if (a.type === 'email') setCompose({ replyTo: null });
              else if (a.type === 'workflow') setActiveTab('workflow');
              else if (a.type === 'note') setActiveTab('notes');
              else if (a.type === 'call' || a.type === 'qualify' || a.type === 'wait') setActiveTab('activities');
            }} />

            <div className="border-b border-[var(--border-card)] flex items-center gap-1 overflow-x-auto">
              {TABS.map((tb) => {
                const Icon = tb.icon;
                const active = activeTab === tb.id;
                const badge = tb.id === 'mails' && counts.unread_inbound > 0 ? counts.unread_inbound : null;
                return (
                  <button
                    key={tb.id}
                    onClick={() => setActiveTab(tb.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium transition-colors border-b-2 -mb-px ${
                      active ? 'text-[#338edc] border-[#338edc]' : 'text-[var(--text-secondary)] border-transparent hover:text-[var(--text-primary)]'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {tb.label}
                    {badge != null && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold">
                        {badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div>
              {activeTab === 'mails' && (
                <MailsTab leadId={leadId} onCounts={setCounts} refreshKey={refreshKey} onReply={onReply} />
              )}
              {activeTab === 'notes' && (
                <NotesTab leadId={leadId} notes={(lead.aktivitaeten || []).filter((a) => a.typ === 'notiz')} onChanged={refresh} currentUser={currentUser} />
              )}
              {activeTab === 'meetings' && (
                <MeetingsTab leadId={leadId} lead={lead} onChanged={refresh} />
              )}
              {activeTab === 'workflow' && (
                <WorkflowTab
                  leadId={leadId}
                  executions={lead.workflow_executions || []}
                  workflows={lead.available_workflows || []}
                  onChanged={refresh}
                />
              )}
              {activeTab === 'activities' && (
                <ActivitiesTab aktivitaeten={lead.aktivitaeten || []} />
              )}
            </div>
          </div>
        </main>

        <Sidebar lead={lead} onChanged={refresh} onRerunAnalyse={handleRerunAnalyse} />
      </div>

      <FloatingActionButton onAction={handleAction} />

      {compose && (
        <ComposeModal
          lead={lead}
          replyTo={compose.replyTo}
          onClose={() => setCompose(null)}
          onSent={refresh}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={() => setConfirmDelete(false)}>
          <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[14px] font-semibold text-[var(--text-primary)] mb-2">Lead löschen?</h3>
            <p className="text-[13px] text-[var(--text-secondary)] mb-4">{lead.firma || lead.ansprechpartner || `Lead #${lead.id}`} wird endgültig entfernt.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(false)} className="px-3 py-2 text-[12px] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] rounded-lg">Abbrechen</button>
              <button onClick={handleDelete} className="px-4 py-2 rounded-lg bg-red-500 text-white text-[12px] font-medium hover:opacity-90">Löschen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
